import { useMemo, useCallback } from 'react';

export default function useRoom({ updateOrderRow, hotelOrderRows, selections, order, hotelAllotments }) {
  const rooms = useMemo(() => hotelOrderRows.flatMap(or => or.rooms), [hotelOrderRows]);

  const roomSizes = useMemo(() => {
    if (selections && selections?.length === 1) {
      const hotelAllotment = hotelAllotments.find(ha => ha.hotelRoomTypeId === selections[0]);
      return hotelAllotment?.hotelAllotmentPricing.map(hap => hap.roomSize) ?? [];
    }
    const hotelSizes = hotelAllotments.map(ha => ha.hotelAllotmentPricing.map(hap => hap.roomSize)).flat();
    return Array.from(new Set(hotelSizes));
  }, [hotelAllotments]);

  const newRoomId = useCallback(() => {
    const roomsIds = rooms.filter(room => room.roomId < 0).map(room => room.roomId);
    if (!roomsIds.length) return -1;
    const newRoomId = (Math.min(...roomsIds) ?? 0) - 1;
    return newRoomId;
  }, [rooms]);

  const handleChange = useCallback(
    (room, hotelGroupId, remove = false) => {
      const orderRowsForGroup = hotelOrderRows.filter(row => row.hotelGroupId === hotelGroupId);
      const roomId = room.roomId ?? newRoomId();

      // When rooms is created they are assign with unique ids and demand more logic to spread the changes over the orderRows in the group that includes the target room entry
      if (roomId > 0) {
        const oldRoomEntry = rooms.find(r => r.roomId === roomId);
        const affectedRooms = rooms.filter(r => r.orderId === oldRoomEntry?.orderId && r.roomMate === oldRoomEntry?.roomMate);
        if (remove) {
          orderRowsForGroup?.forEach(orderRow => {
            const updatedRooms = orderRow.rooms.filter(r => {
              if (affectedRooms.some(affectedRoom => affectedRoom.roomId === r.roomId)) {
                return false;
              }
              return true;
            });
            updateOrderRow({ ...orderRow, rooms: updatedRooms }, false, order.orderId);
          });
        } else {
          orderRowsForGroup?.forEach(orderRow => {
            const updatedRooms = orderRow.rooms.map(r => {
              if (affectedRooms.some(affectedRoom => affectedRoom.roomId === r.roomId)) {
                return { ...r, roomMate: room.roomMate, orderId: room.orderId, external: room.external };
              }
              return r;
            });

            if (
              !updatedRooms.some(
                ur => ur.roomMate === room.roomMate && ur.orderId === room.orderId && ur.external === room.external && ur.orderRowId === orderRow.orderRowId
              )
            ) {
              updatedRooms.push({ ...room, orderRowId: orderRow.orderRowId, roomId: newRoomId() });
            }

            updateOrderRow({ ...orderRow, rooms: updatedRooms }, false, order.orderId);
          });
        }
      } else {
        orderRowsForGroup?.forEach(orderRow => {
          if (remove) {
            const updatedRooms = orderRow.rooms.filter(r => r.roomId !== roomId);
            updateOrderRow({ ...orderRow, rooms: updatedRooms }, false, order.orderId);
          } else {
            const matchingRoomIndex = orderRow.rooms.findIndex(r => r.roomId === roomId);
            if (matchingRoomIndex !== -1) {
              const updatedRooms = [...orderRow.rooms];
              updatedRooms[matchingRoomIndex] = { ...room, orderRowId: orderRow.orderRowId };
              updateOrderRow({ ...orderRow, rooms: updatedRooms }, false, order.orderId);
            } else {
              const updatedRooms = [...orderRow.rooms, { ...room, orderRowId: orderRow.orderRowId, roomId }];
              updateOrderRow({ ...orderRow, rooms: updatedRooms }, false, order.orderId);
            }
          }
        });
      }
    },
    [hotelOrderRows, updateOrderRow, rooms, order, newRoomId]
  );

  return { rooms, onChange: handleChange, roomSizes };
}
